const LIQUID_FILTER_ALIASES = {
  syrup: ['syrup', 'suspension', 'solution', 'elixir', 'liquid'],
  liquid: ['syrup', 'suspension', 'solution', 'elixir', 'liquid'],
  elixir: ['elixir', 'syrup', 'solution', 'suspension'],
};

const DF_START = /\b(Oral|Injectable|Injection|Rectal|Vaginal|Topical|Transdermal|Nasal|Ophthalmic|Otic|Inhalation|Chewable|Disintegrating|Extended|Delayed|Metered|Dry|Prefilled|Auto-Injector)\b/i;

const isOralLiquidName = (name) => /oral (suspension|solution|syrup)|elixir/i.test(String(name || ''));

const formatQty = (value) => {
  if (!Number.isFinite(value)) return '';
  return Number.isInteger(value) ? String(value) : String(Math.round(value * 1000) / 1000);
};

export const liquidEquivalents = (name) => {
  const text = String(name || '');
  const match = text.match(/(\d+(?:\.\d+)?)\s*MG\s*\/\s*ML/i);
  if (!match || !isOralLiquidName(text)) return [];
  const per5 = Number(match[1]) * 5;
  const qty = formatQty(per5);
  if (!qty) return [];
  return [`${qty} mg/5 mL`, `${qty}mg/5ml`, `${qty} mg/5ml`];
};

export const scdFilterHaystack = (scd) => {
  const name = scd?.name || '';
  const parts = [name, scd?.synonym, ...(scd?.hints || []), ...liquidEquivalents(name)];
  if (isOralLiquidName(name)) parts.push('syrup', 'liquid');
  return parts.filter(Boolean).join(' ').toLowerCase();
};

export const scdMatchesFilter = (scd, rawFilter) => {
  const filter = String(rawFilter || '').trim().toLowerCase();
  if (!filter) return true;
  const haystack = scdFilterHaystack(scd);
  const aliases = LIQUID_FILTER_ALIASES[filter];
  if (aliases) return aliases.some((term) => haystack.includes(term));
  const tokens = filter.split(/\s+/).filter(Boolean);
  if (tokens.length > 1 && tokens.every((token) => haystack.includes(token))) return true;
  return haystack.includes(filter);
};

export const scdSecondaryLabel = (scd) => {
  const bits = [];
  const equivalent = liquidEquivalents(scd?.name)[0];
  if (equivalent) bits.push(equivalent);
  if (/oral suspension/i.test(scd?.name || '')) bits.push('often called syrup');
  if (scd?.synonym) bits.push(scd.synonym);
  return bits.join(' · ');
};

export const groupScds = (scds) => {
  const groups = new Map();
  for (const scd of scds) {
    const name = String(scd.name || '');
    const idx = name.search(DF_START);
    const doseForm = idx >= 0 ? name.slice(idx) : 'Other';
    if (!groups.has(doseForm)) groups.set(doseForm, []);
    groups.get(doseForm).push(scd);
  }
  return Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0]));
};

export const catalogNameFromRxNorm = (name) => {
  const text = String(name || '').trim();
  if (!text) return '';
  const letters = text.replace(/[^A-Za-z]/g, '');
  const foldCase = letters.length > 1 && (letters === letters.toUpperCase() || letters === letters.toLowerCase());
  const rest = foldCase ? text.slice(1).toLowerCase() : text.slice(1);
  return text.charAt(0).toUpperCase() + rest;
};

export const normalizeStrengthUnits = (raw) => {
  let text = String(raw || '').trim();
  if (!text) return '';
  text = text.replace(/[µμ]g/gi, 'mcg');
  text = text.replace(
    /(\d+(?:\.\d+)?)[\s-]*(mg|mcg)[\s]*\/[\s]*(\d+(?:\.\d+)?)[\s]*(ml)\b/gi,
    (_, qty, mass, den) => `${qty} ${mass.toLowerCase() === 'mcg' ? 'mcg' : 'mg'}/${den} mL`
  );
  text = text.replace(
    /(\d+(?:\.\d+)?)[\s-]*(mg|mcg)[\s]*\/[\s]*(ml)\b/gi,
    (_, qty, mass) => `${qty} ${mass.toLowerCase() === 'mcg' ? 'mcg/mL' : 'mg/mL'}`
  );
  text = text.replace(/\bmilligrams?\b/gi, 'mg');
  text = text.replace(/\bmicrograms?\b/gi, 'mcg');
  text = text.replace(/\bmillilit(?:er|re)s?\b/gi, 'mL');
  text = text.replace(/\bmcg\b/gi, 'mcg');
  text = text.replace(/\bmg\b/gi, 'mg');
  text = text.replace(/\bml\b/gi, 'mL');
  text = text.replace(/(\d)(mg|mcg|mL)\b/g, '$1 $2');
  return text;
};
